import React, { useState, useEffect } from 'react';
import { bugReportsAPI, adminAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { 
  Bug, 
  ChevronLeft, 
  ChevronRight, 
  ChevronsLeft, 
  ChevronsRight,
  ExternalLink,
  CheckCircle,
  Clock,
  AlertCircle,
  XCircle,
  Loader2
} from 'lucide-react';

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: AlertCircle },
  in_progress: { label: 'In Progress', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Clock },
  resolved: { label: 'Resolved', color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
  closed: { label: 'Closed', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30', icon: XCircle },
};

const PRIORITY_CONFIG = {
  low: { label: 'Low', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
  medium: { label: 'Medium', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  high: { label: 'High', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
  critical: { label: 'Critical', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
};

const BugReports = () => {
  const [bugReports, setBugReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [pagination, setPagination] = useState({
    total: 0,
    total_pages: 1,
    has_next: false,
    has_prev: false
  });
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [selectedBug, setSelectedBug] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [editForm, setEditForm] = useState({
    status: '',
    priority: '',
    assigned_to: '',
    resolution_notes: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadBugReports();
  }, [page, limit, statusFilter, priorityFilter]);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadBugReports = async () => {
    try {
      setLoading(true);
      const params = { page, limit };
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      
      const response = await bugReportsAPI.list(params);
      setBugReports(response.data.data);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to load bug reports:', error);
      toast.error('Failed to load bug reports');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await adminAPI.listUsers();
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const handleViewBug = (bug) => {
    setSelectedBug(bug);
    setEditForm({
      status: bug.status,
      priority: bug.priority,
      assigned_to: bug.assigned_to || '',
      resolution_notes: bug.resolution_notes || ''
    });
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    if (!selectedBug) return;
    
    try {
      setSaving(true);
      await bugReportsAPI.update(selectedBug.id, editForm);
      toast.success('Bug report updated');
      setEditDialogOpen(false);
      loadBugReports();
    } catch (error) {
      console.error('Failed to update bug report:', error);
      toast.error('Failed to update bug report');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(parseISO(dateStr), 'MMM d, yyyy h:mm a');
    } catch {
      return dateStr;
    }
  };

  const renderPagination = () => {
    const { total, total_pages, has_next, has_prev } = pagination;
    const startItem = (page - 1) * limit + 1;
    const endItem = Math.min(page * limit, total);

    return (
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-3 border-t border-border/50">
        <div className="text-sm text-muted-foreground">
          Showing <span className="font-medium text-foreground">{total > 0 ? startItem : 0}</span> to{' '}
          <span className="font-medium text-foreground">{endItem}</span> of{' '}
          <span className="font-medium text-foreground">{total}</span> bug reports
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Rows:</span>
            <Select value={limit.toString()} onValueChange={(val) => { setLimit(parseInt(val)); setPage(1); }}>
              <SelectTrigger className="w-16 h-8 bg-background/50">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPage(1)} disabled={!has_prev}>
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPage(page - 1)} disabled={!has_prev}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="px-3 text-sm">Page {page} of {total_pages}</span>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPage(page + 1)} disabled={!has_next}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPage(total_pages)} disabled={!has_next}>
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="bug-reports-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-red-500/20 flex items-center justify-center">
            <Bug className="h-6 w-6 text-red-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Bug Reports</h1>
            <p className="text-muted-foreground">Track and manage reported issues</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-border/50 bg-card/50">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Status:</span>
              <Select value={statusFilter} onValueChange={(val) => { setStatusFilter(val === 'all' ? '' : val); setPage(1); }}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Priority:</span>
              <Select value={priorityFilter} onValueChange={(val) => { setPriorityFilter(val === 'all' ? '' : val); setPage(1); }}>
                <SelectTrigger className="w-[130px]">
                  <SelectValue placeholder="All priorities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All priorities</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bug Reports Table */}
      <Card className="border-border/50 bg-card/50">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : bugReports.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border/50">
                      <TableHead>Status</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead className="w-[40%]">Description</TableHead>
                      <TableHead>Reported By</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Assigned To</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bugReports.map((bug) => {
                      const statusConfig = STATUS_CONFIG[bug.status] || STATUS_CONFIG.open;
                      const priorityConfig = PRIORITY_CONFIG[bug.priority] || PRIORITY_CONFIG.medium;
                      const StatusIcon = statusConfig.icon;
                      
                      return (
                        <TableRow
                          key={bug.id}
                          className="border-border/50 hover:bg-muted/30 cursor-pointer"
                          onClick={() => handleViewBug(bug)}
                          data-testid={`bug-row-${bug.id}`}
                        >
                          <TableCell>
                            <Badge variant="outline" className={statusConfig.color}>
                              <StatusIcon className="h-3 w-3 mr-1" />
                              {statusConfig.label}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={priorityConfig.color}>
                              {priorityConfig.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="max-w-[400px]">
                            <p className="truncate">{bug.description}</p>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {bug.reported_by_name}
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {formatDate(bug.created_at)}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {bug.assigned_to_name || '-'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              {renderPagination()}
            </>
          ) : (
            <div className="text-center py-12">
              <Bug className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="text-muted-foreground">No bug reports found</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Bug Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bug className="h-5 w-5 text-red-400" />
              Bug Report Details
            </DialogTitle>
            <DialogDescription>
              Review and update the bug report status
            </DialogDescription>
          </DialogHeader>

          {selectedBug && (
            <div className="space-y-4 py-4">
              {/* Description */}
              <div className="space-y-2">
                <Label>Description</Label>
                <div className="p-3 rounded-md bg-muted/30 text-sm whitespace-pre-wrap">
                  {selectedBug.description}
                </div>
              </div>

              {/* Page URL */}
              {selectedBug.page_url && (
                <div className="space-y-2">
                  <Label>Page URL</Label>
                  <div className="flex items-center gap-2">
                    <code className="text-xs bg-muted/30 px-2 py-1 rounded flex-1 truncate">
                      {selectedBug.page_url}
                    </code>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8"
                      onClick={() => window.open(selectedBug.page_url, '_blank')}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Meta info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-muted-foreground">Reported By</Label>
                  <p>{selectedBug.reported_by_name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Reported At</Label>
                  <p>{formatDate(selectedBug.created_at)}</p>
                </div>
              </div>

              {/* Browser Info */}
              {selectedBug.browser_info && (
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Browser Info</Label>
                  <code className="text-xs bg-muted/30 px-2 py-1 rounded block overflow-x-auto">
                    {selectedBug.browser_info}
                  </code>
                </div>
              )}

              <hr className="border-border/50" />

              {/* Status & Priority */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select 
                    value={editForm.status} 
                    onValueChange={(val) => setEditForm({ ...editForm, status: val })}
                  >
                    <SelectTrigger id="status">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="priority">Priority</Label>
                  <Select 
                    value={editForm.priority} 
                    onValueChange={(val) => setEditForm({ ...editForm, priority: val })}
                  >
                    <SelectTrigger id="priority">
                      <SelectValue placeholder="Select priority" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Assigned To */}
              <div className="space-y-2">
                <Label htmlFor="assigned_to">Assign To</Label>
                <Select 
                  value={editForm.assigned_to || "unassigned"} 
                  onValueChange={(val) => setEditForm({ ...editForm, assigned_to: val === "unassigned" ? "" : val })}
                >
                  <SelectTrigger id="assigned_to">
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {users.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Resolution Notes */}
              <div className="space-y-2">
                <Label htmlFor="resolution_notes">Resolution Notes</Label>
                <Textarea
                  id="resolution_notes"
                  placeholder="Add notes about the resolution..."
                  value={editForm.resolution_notes}
                  onChange={(e) => setEditForm({ ...editForm, resolution_notes: e.target.value })}
                  className="min-h-[80px]"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BugReports;
